import React, { useMemo } from 'react'
import { Clock, MapPin, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  convertToMultipleTimezones,
  BUSINESS_TIMEZONES,
  type MultiTimezoneConversion,
  type TeamMemberTimezone
} from '@/lib/timezone-utils'

/**
 * Component that displays time conversions across multiple timezones
 * with business hours awareness and team coordination features
 */

interface MultiTimezoneTooltipProps {
  /** The date/time to convert across timezones */
  date: Date | string
  /** List of timezones to show (defaults to business timezones) */
  timezones?: string[]
  /** Team member configurations for custom business hours */
  teamMembers?: TeamMemberTimezone[]
  /** Whether to show business hours indicators */
  showBusinessHours?: boolean
  /** Whether to show warning indicators */
  showWarnings?: boolean
  /** Maximum number of timezones to display */
  maxTimezones?: number
  /** Size variant for the tooltip content */
  size?: 'sm' | 'md' | 'lg'
  /** Custom trigger element */
  trigger?: React.ReactNode
  /** Whether to show timezone abbreviations */
  showAbbreviations?: boolean
  /** Whether to show UTC offset */
  showOffsets?: boolean
  /** Custom className for the trigger */
  className?: string
  /** Side to position the tooltip */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Whether tooltip is disabled */
  disabled?: boolean
}

export function MultiTimezoneTooltip({
  date,
  timezones = BUSINESS_TIMEZONES.slice(0, 6).map(tz => tz.value), // Top 6 business timezones
  teamMembers,
  showBusinessHours = true,
  showWarnings = true,
  maxTimezones = 8,
  size = 'md',
  trigger,
  showAbbreviations = true,
  showOffsets = true,
  className,
  side = 'top',
  disabled = false
}: MultiTimezoneTooltipProps) {
  // Convert to multiple timezones
  const conversions = useMemo(() => {
    const limitedTimezones = timezones.slice(0, maxTimezones)
    return convertToMultipleTimezones(date, limitedTimezones, teamMembers)
  }, [date, timezones, teamMembers, maxTimezones])

  // Analyze the conversions for summary stats
  const analysis = useMemo(() => {
    const businessHoursCount = conversions.filter(c => c.isBusinessHours).length
    const weekendCount = conversions.filter(c => c.isWeekend).length
    const warningCount = conversions.filter(c => c.warningLevel === 'warning').length
    const cautionCount = conversions.filter(c => c.warningLevel === 'caution').length

    return {
      total: conversions.length,
      businessHoursCount,
      weekendCount,
      warningCount,
      cautionCount,
      businessHoursRatio: conversions.length > 0 ? businessHoursCount / conversions.length : 0
    }
  }, [conversions])

  // Size-based styling
  const sizeClasses = {
    sm: {
      text: 'text-xs',
      spacing: 'space-y-1',
      padding: 'p-2',
      maxWidth: 'max-w-xs'
    },
    md: {
      text: 'text-sm',
      spacing: 'space-y-2',
      padding: 'p-3',
      maxWidth: 'max-w-sm'
    },
    lg: {
      text: 'text-sm',
      spacing: 'space-y-3',
      padding: 'p-4',
      maxWidth: 'max-w-md'
    }
  }

  const currentSize = sizeClasses[size]

  // Default trigger if none provided
  const defaultTrigger = (
    <div className={cn('cursor-help inline-flex items-center gap-1', className)}>
      <Clock className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {conversions.length} timezone{conversions.length !== 1 ? 's' : ''}
      </span>
    </div>
  )

  if (disabled) {
    return trigger || defaultTrigger
  }

  // Render individual timezone conversion
  const renderTimezoneConversion = (conversion: MultiTimezoneConversion) => {
    const getWarningIcon = () => {
      if (!showWarnings) return null
      
      switch (conversion.warningLevel) {
        case 'warning':
          return <AlertTriangle className="h-3 w-3 text-amber-500" />
        case 'caution':
          return <Info className="h-3 w-3 text-blue-500" />
        default:
          return showBusinessHours && conversion.isBusinessHours ? 
            <CheckCircle className="h-3 w-3 text-green-500" /> : null
      }
    }

    const getTimezoneLabel = () => {
      // Try to get a team member name first
      const teamMember = teamMembers?.find(member => member.timezone === conversion.timezone)
      if (teamMember?.name) {
        return teamMember.name
      }
      
      // Fall back to business timezone label or display name
      const businessTz = BUSINESS_TIMEZONES.find(tz => tz.value === conversion.timezone)
      return businessTz?.label || conversion.displayName
    }

    return (
      <div 
        key={conversion.timezone} 
        className="flex items-center justify-between gap-3 py-1"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {getWarningIcon()}
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">
              {getTimezoneLabel()}
            </div>
            {(showAbbreviations || showOffsets) && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                {showAbbreviations && <span>{conversion.abbreviation}</span>}
                {showAbbreviations && showOffsets && <span>•</span>}
                {showOffsets && <span>{conversion.offset}</span>}
              </div>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="font-semibold">
            {conversion.formatted}
          </div>
          {showWarnings && conversion.warningMessage && (
            <div className="text-xs text-muted-foreground">
              {conversion.warningMessage}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Render summary header
  const renderSummary = () => {
    if (!showBusinessHours && !showWarnings) return null

    const getSummaryColor = () => {
      if (analysis.warningCount > 0) return 'text-amber-600'
      if (analysis.businessHoursRatio >= 0.8) return 'text-green-600'
      if (analysis.businessHoursRatio >= 0.5) return 'text-blue-600'
      return 'text-muted-foreground'
    }

    const getSummaryText = () => {
      if (analysis.businessHoursRatio === 1) return 'Perfect for all timezones'
      if (analysis.businessHoursRatio >= 0.8) return 'Good for most timezones'
      if (analysis.businessHoursRatio >= 0.5) return 'Acceptable for some timezones'
      if (analysis.weekendCount > analysis.total / 2) return 'Weekend for most timezones'
      return 'Challenging for most timezones'
    }

    return (
      <div className="border-b pb-2 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Global Time</span>
          </div>
          <Badge variant="secondary" className={cn('text-xs', getSummaryColor())}>
            {analysis.businessHoursCount}/{analysis.total} business hours
          </Badge>
        </div>
        {(analysis.warningCount > 0 || analysis.weekendCount > 0) && (
          <div className="mt-1 text-xs text-muted-foreground">
            {getSummaryText()}
          </div>
        )}
      </div>
    )
  }

  // Tooltip content
  const tooltipContent = (
    <div className={cn(currentSize.maxWidth, currentSize.padding)}>
      <div className={cn(currentSize.text, currentSize.spacing)}>
        {renderSummary()}
        
        <div className="space-y-1">
          {conversions.map((conversion) => 
            renderTimezoneConversion(conversion)
          )}
        </div>
        
        {timezones.length > maxTimezones && (
          <div className="pt-2 mt-2 border-t text-xs text-muted-foreground text-center">
            +{timezones.length - maxTimezones} more timezone{timezones.length - maxTimezones !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {trigger || defaultTrigger}
        </TooltipTrigger>
        <TooltipContent side={side} className="border-none shadow-lg">
          <Card className="border-0 shadow-none">
            {tooltipContent}
          </Card>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Compact version for inline usage
 */
export function CompactMultiTimezoneTooltip({
  date,
  timezones,
  teamMembers,
  className,
  ...props
}: Omit<MultiTimezoneTooltipProps, 'size' | 'trigger'> & {
  className?: string
}) {
  const conversions = useMemo(() => {
    const limitedTimezones = (timezones || BUSINESS_TIMEZONES.slice(0, 4).map(tz => tz.value)).slice(0, 4)
    return convertToMultipleTimezones(date, limitedTimezones, teamMembers)
  }, [date, timezones, teamMembers])

  const businessHoursCount = conversions.filter(c => c.isBusinessHours).length
  const warningCount = conversions.filter(c => c.warningLevel === 'warning').length

  const getIndicatorColor = () => {
    if (warningCount > 0) return 'bg-amber-500'
    if (businessHoursCount === conversions.length) return 'bg-green-500'
    if (businessHoursCount >= conversions.length / 2) return 'bg-blue-500'
    return 'bg-gray-400'
  }

  const trigger = (
    <div className={cn('inline-flex items-center gap-1 cursor-help', className)}>
      <div className={cn('w-2 h-2 rounded-full', getIndicatorColor())} />
      <span className="text-xs text-muted-foreground">
        {conversions.length}TZ
      </span>
    </div>
  )

  return (
    <MultiTimezoneTooltip
      date={date}
      timezones={timezones}
      teamMembers={teamMembers}
      size="sm"
      trigger={trigger}
      {...props}
    />
  )
}

export default MultiTimezoneTooltip